import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import PlaylistSetupForm from '../components/PlaylistSetupForm';
import './PlaylistSetupPage.css';

const CODE_REGEX = /^[A-Z0-9]{6}$/;
const SUBMITTED_STORAGE_PREFIX = 'tellynite_playlist_setup_done_';

function doneStorageKey(code) {
  return `${SUBMITTED_STORAGE_PREFIX}${code}`;
}

function wasCodeSubmitted(code) {
  try {
    return sessionStorage.getItem(doneStorageKey(code)) === '1';
  } catch {
    return false;
  }
}

function markCodeSubmitted(code) {
  try {
    sessionStorage.setItem(doneStorageKey(code), '1');
  } catch {
    // Private mode can block sessionStorage; success URL still works for this tab.
  }
}

function readPageState(searchParams) {
  if (searchParams.get('submitted') === '1') {
    return { submitted: true, code: null, invalidQuery: false, needsSubmittedParam: false };
  }

  const rawCode = searchParams.get('code');
  if (rawCode == null || rawCode.trim() === '') {
    return { submitted: false, code: null, invalidQuery: false, needsSubmittedParam: false };
  }

  const setupCode = rawCode.toUpperCase();
  if (CODE_REGEX.test(setupCode)) {
    if (wasCodeSubmitted(setupCode)) {
      return { submitted: true, code: null, invalidQuery: false, needsSubmittedParam: true };
    }
    return { submitted: false, code: setupCode, invalidQuery: false, needsSubmittedParam: false };
  }

  return { submitted: false, code: null, invalidQuery: true, needsSubmittedParam: false };
}

function PageShell({ children, centered = false }) {
  return (
    <>
      <div className="tellynite-banner">
        <img src="/tellynite-logo.png" alt="TellyNite" className="tellynite-logo" />
      </div>
      <div className={centered ? 'landing-container' : 'page-container'}>
        {children}
      </div>
    </>
  );
}

const PlaylistSetupPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readPageState(searchParams);
  const [code, setCode] = useState(initial.code);
  const [submitted, setSubmitted] = useState(initial.submitted);
  const [invalidQuery, setInvalidQuery] = useState(initial.invalidQuery);
  const [manualCode, setManualCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  useEffect(() => {
    const next = readPageState(searchParams);
    setSubmitted(next.submitted);
    setCode(next.code);
    setInvalidQuery(next.invalidQuery);
    if (next.needsSubmittedParam) {
      setSearchParams({ submitted: '1' }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmitted = (usedCode) => {
    const normalized = (usedCode || '').toUpperCase();
    if (CODE_REGEX.test(normalized)) {
      markCodeSubmitted(normalized);
    }
    setSubmitted(true);
    setCode(null);
    setInvalidQuery(false);
    setSearchParams({ submitted: '1' }, { replace: true });
  };

  const clearInvalidQuery = () => {
    if (invalidQuery) setInvalidQuery(false);
  };

  const handleManualBoxChange = (idx, val) => {
    clearInvalidQuery();
    if (!val) {
      updateManualCode(idx, '');
      return;
    }
    const char = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 1);
    if (!char) return;
    updateManualCode(idx, char);
    if (idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleManualPaste = (idx, e) => {
    const pasted = (e.clipboardData?.getData('text') || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    if (!pasted) return;
    e.preventDefault();
    clearInvalidQuery();
    setManualCode(prev => {
      const next = [...prev];
      for (let i = 0; i < pasted.length && idx + i < 6; i += 1) {
        next[idx + i] = pasted[i];
      }
      return next;
    });
    const focusIndex = Math.min(idx + pasted.length, 5);
    requestAnimationFrame(() => inputRefs.current[focusIndex]?.focus());
  };

  const handleManualBoxKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !manualCode[idx] && idx > 0) {
      updateManualCode(idx - 1, '');
      inputRefs.current[idx - 1]?.focus();
      e.preventDefault();
    }
    if (e.key === 'ArrowLeft' && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
      e.preventDefault();
    }
    if (e.key === 'ArrowRight' && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
      e.preventDefault();
    }
  };

  const updateManualCode = (idx, val) => {
    setManualCode(prev => {
      const arr = [...prev];
      arr[idx] = val;
      return arr;
    });
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const codeStr = manualCode.join('').toUpperCase();
    if (CODE_REGEX.test(codeStr)) {
      setInvalidQuery(false);
      setSearchParams({ code: codeStr }, { replace: true });
    }
  };

  if (submitted) {
    return (
      <PageShell>
        <div className="playlist-setup-container">
          <div className="header">
            <h1>TellyNite Playlist Setup</h1>
          </div>
          <div className="playlist-form success-panel">
            <div className="status-message success" role="status">
              Playlist submitted! Please check your TV app to continue.
              <span className="status-note">You can close this page now.</span>
            </div>
            <p className="success-home-link">
              <a href="https://tellynite.com">Visit tellynite.com</a>
            </p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (!code) {
    return (
      <PageShell centered>
        <div className="landing-card">
          <h1>TellyNite Playlist Setup</h1>
          <p className="landing-hint">
            Enter the 6-character code shown on your TV.
          </p>
          {invalidQuery && (
            <div className="error-message" role="alert">
              That code isn’t valid. Check the code on your TV and try again.
            </div>
          )}
          <form className="manual-code-form" onSubmit={handleManualSubmit}>
            <div className="manual-code-boxes" role="group" aria-label="Setup code">
              {manualCode.map((val, idx) => (
                <input
                  key={idx}
                  type="text"
                  className="manual-code-box"
                  maxLength={1}
                  value={val}
                  onChange={e => handleManualBoxChange(idx, e.target.value)}
                  onPaste={e => handleManualPaste(idx, e)}
                  onKeyDown={e => handleManualBoxKeyDown(idx, e)}
                  ref={el => { inputRefs.current[idx] = el; }}
                  autoFocus={idx === 0}
                  inputMode="text"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                  spellCheck={false}
                  aria-label={`Code character ${idx + 1}`}
                  pattern="[A-Za-z0-9]"
                />
              ))}
            </div>
            <button
              type="submit"
              className="manual-code-submit"
              disabled={manualCode.join('').length !== 6}
            >
              Continue
            </button>
          </form>
          <p className="landing-note">
            You can also scan the QR code in the TellyNite TV app.
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PlaylistSetupForm code={code} onSubmitted={handleSubmitted} />
    </PageShell>
  );
};

export default PlaylistSetupPage;
